if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;

        const sectionRoot = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const addonsEl = sectionRoot?.querySelector('[data-jaromin-travel-addons]');
        const bagVariantId = addonsEl?.querySelector('[data-jaromin-bag-select]')?.value;
        const strapVariantId = addonsEl?.querySelector('[data-jaromin-strap-select]')?.value;

        const addonItems = [];
        if (bagVariantId) addonItems.push({ id: bagVariantId, quantity: 1 });
        if (strapVariantId) addonItems.push({ id: strapVariantId, quantity: 1 });

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        let config;
        if (addonItems.length > 0) {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['Content-Type'] = 'application/json';
          const payload = {
            items: [{ id: variantId, quantity }, ...addonItems],
          };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(payload);
        } else {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            if (addonItems.length > 0 && !response.sections) {
              const sectionIds = this.cart.getSectionsToRender().map((section) => section.id).join(',');
              const sectionsUrl = `${routes.cart_url}?sections=${sectionIds}&sections_url=${encodeURIComponent(window.location.pathname)}`;
              return fetch(sectionsUrl)
                .then((res) => res.json())
                .then((sections) => {
                  const first = response.items?.[0] || response;
                  return { ...first, sections };
                })
                .then((cartRenderPayload) => this.finishCartAdd(cartRenderPayload, variantId, linesUpdateDeferred, evt));
            }

            const cartRenderPayload = response.sections ? response : this.normalizeMultiAddResponse(response);
            this.finishCartAdd(cartRenderPayload, variantId, linesUpdateDeferred, evt);
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      finishCartAdd(cartRenderPayload, variantId, linesUpdateDeferred, evt) {
        this.resolveCartLinesUpdate(linesUpdateDeferred);

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: cartRenderPayload,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(cartRenderPayload);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(cartRenderPayload);
          });
        }
      }

      normalizeMultiAddResponse(response) {
        if (response.sections) return response;
        const first = response.items?.[0];
        return first ? { ...first, sections: response.sections } : response;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
